// Kubernetes namespace names must be a valid RFC 1123 DNS label.
// https://kubernetes.io/docs/concepts/overview/working-with-objects/names/#dns-label-names
export const NAMESPACE_NAME_REGEX = /^[a-z0-9]([-a-z0-9]*[a-z0-9])?$/;
export const NAMESPACE_NAME_MAX_LENGTH = 63;

export const INVALID_NAMESPACE_NAME_MESSAGE =
  'must consist of lowercase alphanumeric characters or "-", and must start and end with an alphanumeric character (e.g. "my-namespace")';

// sanitizeNamespaceName lowercases and trims a user-provided namespace name so
// that case differences (e.g. "Production" vs "production") don't produce
// spurious validation failures or duplicate namespaces. See C9S-364.
export function sanitizeNamespaceName(value: string): string {
  return value.trim().toLowerCase();
}

export function isValidNamespaceName(value: string): boolean {
  return (
    value.length > 0 &&
    value.length <= NAMESPACE_NAME_MAX_LENGTH &&
    NAMESPACE_NAME_REGEX.test(value)
  );
}

// firstNamespaceError normalizes a Formik/yup validation error for a
// namespaces field into a single readable message, or undefined if there's
// no error. Array-level failures (e.g. "at least one namespace is required")
// surface as a plain string; per-item test failures (our format check, or
// the pre-existing uniqueness check) surface as a sparse array with the
// message at the failing index — either a string directly, or an object
// (e.g. `{ namespace: "..." }`) for array-of-object namespace fields.
export function firstNamespaceError(error: unknown): string | undefined {
  if (typeof error === 'string') {
    return error;
  }
  if (Array.isArray(error)) {
    for (const entry of error) {
      if (typeof entry === 'string') {
        return entry;
      }
      if (entry && typeof entry === 'object') {
        const nested = Object.values(entry).find(
          (value): value is string => typeof value === 'string'
        );
        if (nested) {
          return nested;
        }
      }
    }
  }
  return undefined;
}

// namespaceFieldError reads the `namespaces` validation error off a specific
// row of a Formik array field (e.g. `errors.accessList`) and normalizes it
// via firstNamespaceError. Array-level failures (a plain string, e.g. "at
// least one access is required") aren't tied to a single row, so they're
// not surfaced here — only per-row errors are.
export function namespaceFieldError(
  rowErrors: unknown,
  index: number
): string | undefined {
  if (!Array.isArray(rowErrors)) {
    return undefined;
  }
  const rowError = rowErrors[index];
  if (!rowError || typeof rowError !== 'object') {
    return undefined;
  }
  return firstNamespaceError((rowError as Record<string, unknown>).namespaces);
}
